import type {
  Block, Cell, ChipCell, Column, NoteBlock, SubCell, TableBlock,
} from '@/kova/spec/types'

import { ApiError } from '@/lib/api/errors'

import type { DataSource, ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'

/**
 * Recognition › Candidate queue (`rc-cand`), read from
 * `GET /v1/admin/catalog/candidates` (go-wallet#83, handler
 * `internal/server/admin_review.go`).
 *
 * # What this screen is answerable to
 *
 * Invariant 11: user feedback never mutates the payer catalog. A correction
 * writes `recognition_feedback` and aggregates into `catalog_candidates`, and
 * an operator promotes. The queue is therefore a pile of EVIDENCE, and every
 * rendering decision below follows from refusing to let evidence read as a
 * verdict — the crowd's platform is labelled a suggestion, the count is
 * labelled observations, and nothing on the screen can be pressed to accept
 * either.
 *
 * Invariant 11b is the sharper edge, and it is the one the payload cannot help
 * with. "Which platform pays you through MassPay?" resolves differently per
 * customer, legitimately, so promoting an orchestrator candidate into the
 * global catalog attributes one customer's Fanvue income to another. The wire
 * carries no orchestrator flag — there is nothing in `candidateView` that
 * separates `MASSPAY INC` from `FANVUE LTD` — so the screen cannot mark those
 * rows and must instead say, standing and always, that it cannot. Silence there
 * would be the screen implying every row is safe to promote globally.
 *
 * # The design's `sim` and `actions` blocks, and what replaced them
 *
 * The design draws `table` → `sim` → `actions`: a promotion composer ("Promote
 * candidate 918") and a dismiss control, both singular, both about a SELECTED
 * candidate. This file emitted only the table and said so, on the grounds that
 * a mutation needs its own review.
 *
 * It has one now (#204), and the two decisions live in `catalogcontrols.ts`,
 * reached by OPENING a row rather than by rendering two hundred controls under
 * a hundred-row queue. The design's own singular is the shape that was right.
 *
 * The `sim` block itself stays unemitted, and that half of the old reasoning
 * stands unchanged: its substance is per-signal evidence, a confidence tier, a
 * fuzzy ratio and three sample transaction ids, and no endpoint answers for any
 * of them. The only way to render it is from the corpus, and a promotion screen
 * showing invented evidence beside a real candidate id is the worst thing in
 * this file's reach. So it is absent and a note names it as absent, which is
 * the same rule the dashboard applies to a withheld balance leg: an absence is
 * shown, never closed up as though it were never there.
 */

// ---------------------------------------------------------------------------
// The wire shape — mirrors internal/server/admin_review.go json tags
// ---------------------------------------------------------------------------

/**
 * One aggregated proposal.
 *
 * Field names are the Go tags verbatim. Two of them are load-bearing beyond
 * naming:
 *
 * - `proposed_platform_id`, not `platform_id`. The backend renamed it on
 *   purpose and asserts the rename in a test: an operator promoting states the
 *   platform themselves, and a field named as though it were the answer is how
 *   somebody promotes by pressing enter.
 * - `observation_count` counts OBSERVATIONS — feedback rows plus unrecognised
 *   credit sightings — and not distinct customers. One customer paid forty-one
 *   times raises a count of 41 with a consensus of one.
 */
export interface CandidateView {
  readonly id: number
  readonly candidate_key: string
  readonly proposed_platform_id: string
  readonly observation_count: number
  readonly sample_name: string
  readonly sample_iban: string
  /** Present and empty when the aggregate recorded none. Never defaulted. */
  readonly currency: string
  readonly first_seen_at: string
  readonly last_seen_at: string
}

export interface CandidateQueueResponse {
  /** The whole open queue, not the length of this page. */
  readonly total: number
  readonly candidates: readonly CandidateView[]
}

/**
 * One candidate, as much of it as a CONTROL is allowed to know.
 *
 * Deliberately not `CandidateView`. Two of that type's fields —`sample_iban`
 * and `sample_name` — are a third party's account pseudonym and a third party's
 * name, and `PromoteCandidate` does not read either column on purpose. Handing
 * them to a control would put them one field away from a body, so the standing
 * carries the payer as ONE already-rendered description and neither half
 * separately.
 *
 * `observations` is a rendered phrase rather than a number for the same reason
 * the column is: an unstateable count must not become a zero on the way to a
 * control that quotes it.
 */
export interface CandidateStanding {
  readonly id: number
  readonly candidateKey: string
  /** `proposed_platform_id`. THE CROWD'S, and named to say so everywhere. */
  readonly proposedPlatform: string
  readonly payer: string
  readonly observations: string
  readonly currency: string
}

/**
 * One candidate for a control, or nothing.
 *
 * `null` on any row whose id cannot be stated exactly — the same refusal the
 * table makes, for the same reason: *"the next thing that happens to a copied
 * id is a promotion or a dismissal against it"*, and an id wrong in its last
 * digits promotes a different candidate. A row the table renders as unreadable
 * is a row no control may be composed for.
 */
export function readCandidate(raw: unknown): CandidateStanding | null {
  if (typeof raw !== 'object' || raw === null) return null
  const c = raw as Partial<CandidateView>
  const id = exactInteger(c.id)
  if (id === null) return null

  const name = text(c.sample_name)
  const iban = text(c.sample_iban)
  const count = exactInteger(c.observation_count)

  return {
    id,
    candidateKey: text(c.candidate_key),
    proposedPlatform: text(c.proposed_platform_id),
    payer: name !== '' ? name : iban !== '' ? iban : 'a payer the aggregate kept no name for',
    observations: count === null
      ? 'an unstateable number of times — this is not a count of zero'
      : `${String(count)} time(s)`,
    currency: text(c.currency),
  }
}

export const CANDIDATES_PATH = '/catalog/candidates'

const SOURCE = 'postgres · catalog_candidates (GET /v1/admin/catalog/candidates)'

// ---------------------------------------------------------------------------
// Reading the wire, fail-closed
// ---------------------------------------------------------------------------

/** A string the backend actually sent, or nothing. Never a stand-in. */
function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

/**
 * An integer we can state exactly, or nothing.
 *
 * `JSON.parse` hands back a double for a Go `int64`, so a candidate id past
 * 2^53 has already lost its last digits by the time it reaches here. On this
 * screen that id is a `m`-kind cell with a copy affordance, and the next thing
 * that happens to a copied id is a promotion or a dismissal against it —
 * against a DIFFERENT candidate, plausibly and silently. `null` routes it into
 * the unreadable path instead.
 */
function exactInteger(value: unknown): number | null {
  if (typeof value !== 'number') return null
  return Number.isSafeInteger(value) ? value : null
}

/**
 * Go's zero `time.Time`, which this endpoint can genuinely emit.
 *
 * `candidateView` holds `FirstSeenAt`/`LastSeenAt` as plain `time.Time`, not
 * pointers — unlike `catalogEntryView.EffectiveTo` next door, which is a
 * pointer with a comment saying it exists so "still current" is null "rather
 * than a zero time that a client would render as the year 1". This struct did
 * not get that treatment, so an unset timestamp arrives as
 * `0001-01-01T00:00:00Z` and parses perfectly. It is the one absence that
 * cannot be caught by checking for undefined.
 */
function isZeroInstant(at: Date): boolean {
  return at.getUTCFullYear() <= 1
}

/**
 * An RFC-3339 instant in the format the design draws timestamps in.
 *
 * Delegates to `readAtStamp` so every stamp in the console reads the same way,
 * and hands back the raw string rather than inventing one when the backend
 * sends something unparseable — an ugly stamp is recoverable, a confidently
 * wrong one beside a six-week-old candidate is not.
 */
function stamp(value: unknown): string {
  const iso = text(value)
  if (iso === '') return ''
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return iso
  return isZeroInstant(at) ? '' : readAtStamp(at)
}

/**
 * Which signal identified this payer, from the candidate key's own prefix.
 *
 * `storage.candidateKey` builds `<kind>:<value>|<currency>|<platform>` out of
 * `recognition.CandidateKey`, whose three kinds are `iban`, `name` and
 * `remit`. Only the prefix is read: the normalisers strip every character that
 * is not alphanumeric (plus a few separators for remittance), so neither `:`
 * nor `|` can appear inside the value, and taking the FIRST colon is
 * unambiguous.
 *
 * Parsing a backend-internal key format is coupling, and the alternative was an
 * Evidence column that is blank on every row — this is the only signal the
 * endpoint carries about WHY a payer is one candidate rather than another, and
 * "name fuzzy" versus "IBAN exact" is the difference between a promotion an
 * operator should think about and one they should not. The coupling is made
 * safe by failing to "we cannot read it" rather than to a guess, so a fourth
 * kind added upstream degrades the cell and never mislabels it.
 */
const EVIDENCE: Readonly<Record<string, string>> = {
  iban: 'debtor IBAN, exact',
  name: 'normalised payer name',
  remit: 'remittance text',
}

/** Longer than any kind the backend emits; a key without a colon inside this
 * window is not a key this console can read, and bounding it also stops a
 * malformed key rendering a paragraph into a table cell. */
const MAX_EVIDENCE_KIND = 16

function evidenceOf(key: unknown): string {
  const raw = text(key)
  if (raw === '') return 'no candidate key — the identifying signal is not stated'

  const at = raw.indexOf(':')
  if (at <= 0 || at > MAX_EVIDENCE_KIND) {
    return 'the candidate key names no signal this console can read'
  }

  const kind = raw.slice(0, at)
  // `hasOwnProperty`, not a bare index. A bare `EVIDENCE[kind]` walks the
  // prototype chain, so a key of `toString:…` or `constructor:…` — both well
  // inside the length bound above — resolves to a FUNCTION, which this
  // `: string` signature would hand straight into a table cell. The same guard
  // `reconcilerCard` uses on the dashboard, for the same reason.
  const known = Object.prototype.hasOwnProperty.call(EVIDENCE, kind)
    ? EVIDENCE[kind]
    : undefined

  // Unknown resolves to less confidence, never more — the same posture
  // `availabilityOf` takes on the dashboard. A kind added upstream must read as
  // "we do not know what matched", never as one of the three we do know.
  return known ?? `signal “${kind}” — this console does not know it`
}

/**
 * The candidate's currency scope, as a cell and its caveat.
 *
 * Empty is a real state the backend goes out of its way to preserve — present
 * and empty rather than omitted, so "any currency" and "we did not record one"
 * are not rendered identically. It is never filled in: a EUR that nobody sent
 * is invariant 4 broken in the one place it is cheapest to break it.
 */
function currencyCell(raw: unknown): SubCell {
  const code = text(raw)
  if (code === '') return ['not recorded', 'the aggregate carries no currency']
  // An ISO-4217 alphabetic code is three uppercase letters. Anything else is
  // rendered verbatim and flagged rather than corrected, because a code we do
  // not recognise still identifies the scope to whoever sent it.
  return /^[A-Z]{3}$/.test(code) ? [code] : [code, 'not an ISO-4217 code shape']
}

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

const COLUMNS: readonly Column[] = [
  ['Candidate', 'm', '96px'],
  ['Payer, as it arrived', 's', 'minmax(210px,1.5fr)'],
  ['Occurrences', 'n', '104px'],
  ['Currency', 's', '112px'],
  ['Suggested platform', 's', 'minmax(180px,1.3fr)'],
  ['Signal', 'x', 'minmax(180px,1.2fr)'],
  ['Seen (UTC)', 's', 'minmax(160px,1.1fr)'],
  ['State', 'c', '116px'],
]

interface RowRead {
  readonly cells: readonly Cell[]
  /** What about this row could not be stated, in an operator's words. */
  readonly withheld: readonly string[]
}

/** The payer sample, which is one observation and not the aggregate. */
function payerCell(name: string, iban: string): SubCell {
  if (name !== '' && iban !== '') return [name, iban]
  if (name !== '') return [name, 'no IBAN on the sample']
  if (iban !== '') return [iban, 'no payer name on the sample']
  return ['payer not recorded', 'the aggregate kept neither a name nor an IBAN']
}

/**
 * The crowd's suggestion, labelled as one.
 *
 * The subtitle is not decoration. A platform in a column headed "Suggested
 * platform" still reads as an answer at a glance, and the one behaviour
 * invariant 11 forbids is accepting it as one — so the row says what the value
 * is every time it says the value.
 */
function suggestionCell(platform: string): SubCell {
  return platform === ''
    ? ['—', 'no suggestion · raised by unrecognised credits, not by a customer']
    : [platform, 'crowd suggestion · an operator states the platform themselves']
}

function seenCell(first: string, last: string): SubCell {
  if (last === '' && first === '') return ['not recorded', 'the aggregate carries no timestamps']
  if (last === '') return ['last seen not recorded', first === '' ? '' : `first seen ${first}`]
  return first === '' ? [last, 'first seen not recorded'] : [last, `first seen ${first}`]
}

/**
 * One candidate, as a row.
 *
 * Takes `unknown` rather than `CandidateView` because `Array.isArray` says
 * nothing about the elements. A row that is not an object at all is still
 * rendered — as an unreadable row — because dropping it would shorten a queue
 * an operator is working through and tell them nothing about why.
 */
function readRow(raw: unknown): RowRead {
  if (typeof raw !== 'object' || raw === null) {
    return {
      cells: [
        '—', ['unreadable row', 'the queue returned something that is not a candidate'],
        '—', ['—'], ['—', ''], 'unknown', ['—', ''], ['unreadable', 'r'],
      ],
      withheld: ['a row that did not arrive as a candidate object'],
    }
  }

  const c = raw as Partial<CandidateView>
  const withheld: string[] = []

  const id = exactInteger(c.id)
  const name = text(c.sample_name)
  const iban = text(c.sample_iban)
  // How the row is referred to in the withheld list. The id when we have one;
  // otherwise whatever identifies the payer, so the operator can find the row
  // on screen even when its id is the thing that failed.
  const who = id === null ? (name === '' ? iban === '' ? 'an unidentified row' : iban : name) : `candidate ${String(id)}`

  if (id === null) {
    withheld.push(
      `${who}: the candidate id could not be stated exactly, so it is not shown — a copied id wrong in its last digits promotes a different candidate`,
    )
  }

  const count = exactInteger(c.observation_count)
  if (count === null) {
    // Never a zero here. Zero observations would read as "no evidence at all",
    // which is a claim about the candidate; this is a claim about the read.
    withheld.push(`${who}: the observation count could not be stated. This is not a count of zero.`)
  }

  const currency = currencyCell(c.currency)
  if (text(c.currency) === '') {
    withheld.push(`${who}: no currency was recorded, so the candidate's scope is unknown`)
  }

  const first = stamp(c.first_seen_at)
  const last = stamp(c.last_seen_at)
  if (last === '') {
    withheld.push(`${who}: no last-seen time, so the queue cannot say whether this is still arriving`)
  }

  // The endpoint selects `promoted_at IS NULL AND dismissed_at IS NULL`, so
  // every row it returns is open — that is a property of the read, not a field
  // we are inventing. A row we could not state exactly is not called open,
  // because "open" invites an operator to act on it.
  const degraded = id === null || count === null
  const state: ChipCell = degraded ? ['unreadable', 'r'] : ['open', 'a']

  return {
    cells: [
      id === null ? '—' : String(id),
      payerCell(name, iban),
      count === null ? '—' : String(count),
      currency,
      suggestionCell(text(c.proposed_platform_id)),
      evidenceOf(c.candidate_key),
      seenCell(first, last),
      state,
    ],
    withheld,
  }
}

// ---------------------------------------------------------------------------
// The table
// ---------------------------------------------------------------------------

/**
 * `maxCandidatePage` in `internal/server/admin_review.go`, and the only page
 * size this console can be served: `loadCandidates` sends no `limit`, so the
 * backend uses its own default, which is the cap.
 */
const PAGE_CAP = 100

/**
 * The footer, which is the only place the page's completeness is stated.
 *
 * The endpoint caps a page at 100 and answers the depth from a separate
 * `count(*)`, so a page shorter than the depth is the NORMAL case on a busy
 * queue and has to be said out loud — a table quietly ending at 100 rows is a
 * backlog nobody escalates, which is the exact failure the separate count
 * exists to prevent.
 *
 * The cap is named only when the page actually reached it. A short page under a
 * larger depth is a real state — a race between the two queries, or a page the
 * server chose to shorten — and calling it "capped" would state a CAUSE this
 * console did not read. The worst case of that was the empty page: nought rows
 * under a depth of 34 rendered "the page is capped" directly above a red note
 * saying the two disagree.
 */
function footerFor(shown: number, depth: number | null): string {
  if (depth === null) {
    return `${String(shown)} row(s) shown · the queue depth could not be read, so this may be all of it or the start of it`
  }
  if (shown === depth) return `${String(shown)} of ${String(depth)} open candidates`
  if (shown < depth) {
    return shown >= PAGE_CAP
      ? `${String(shown)} of ${String(depth)} open candidates · the page is capped at ${String(PAGE_CAP)}, the remainder is not shown`
      : `${String(shown)} of ${String(depth)} open candidates · the remainder is not on this page`
  }
  // The rows and the count are two queries against a moving table, so this is
  // reachable without anything being broken. Stated rather than smoothed over:
  // the alternative is silently reporting a depth smaller than the rows under
  // it, which reads as a bug in the console rather than as a promotion landing
  // between two reads.
  return `${String(shown)} rows shown against a queue depth of ${String(depth)} · the page and the count were read a moment apart`
}

function tableBlock(rows: readonly (readonly Cell[])[], depth: number | null): TableBlock {
  return {
    t: 'table',
    title: 'catalog_candidates',
    // The console's three-value source vocabulary has no 'operational' member,
    // and 'ledger' is the word it already uses for our own Postgres — the
    // dashboard maps `authority: operational` onto it. What the badge is for is
    // saying this is not the analytics mirror (law 12), and that is true.
    src: 'ledger',
    note: 'Only candidates an operator has neither promoted nor dismissed. A promoted or dismissed candidate is absent from this read, not shown closed.',
    // The backend orders by observation_count DESC, id — stated as the saved
    // view because the corpus's 'Value desc' describes a sort over a column no
    // endpoint answers for.
    savedView: 'Occurrences desc',
    // No facet bar. The endpoint takes one parameter, `limit`, and nothing
    // else: a chip reading `currency EUR` would claim a filter is applied to a
    // read that is not filtered, and a chip an operator can click that changes
    // no query is worse than no chip.
    facets: false,
    cols: COLUMNS,
    rows,
    footer: footerFor(rows.length, depth),
    foot2: 'select a row to promote or dismiss it · a count is evidence, never consent',
  }
}

// ---------------------------------------------------------------------------
// The standing notes
// ---------------------------------------------------------------------------

/**
 * Invariant 11, on the screen rather than only in a document.
 *
 * Worth the vertical space because the count is the column an operator sorts by
 * and the number they will quote when they promote. Forty-one observations from
 * one customer and forty-one from forty-one customers are the same cell, and
 * the endpoint cannot tell them apart — so the screen says so where the number
 * is, not in a footnote.
 */
const EVIDENCE_NOTE: NoteBlock = {
  t: 'note',
  tone: 'r',
  kicker: 'Invariant 11',
  title: 'A count is evidence, not consent',
  body: 'Occurrences aggregate customer corrections and unrecognised credit sightings for one payer and one proposed platform. They are NOT distinct customers — this read does not count people, so a candidate at 41 may be one customer paid 41 times. Volume prompts a promotion; it never grants one. Promotion is an operator naming the platform, against a queue that only ever suggests.',
  ref: 'recognition_feedback → catalog_candidates → operator promotion',
}

/**
 * Invariant 11b, which the payload cannot help with.
 *
 * The queue is keyed on the payer and the proposed platform, and an
 * orchestrator — MassPay, Payoneer — is a payer like any other. Nothing in the
 * response separates "this payer IS the platform" from "this payer routes for
 * many platforms, differently per customer", and promoting the second into the
 * global catalog attributes one customer's income to another. The console
 * cannot mark those rows, so it says it cannot; leaving the note out would let
 * the screen imply every row is safe to promote globally.
 */
const ORCHESTRATOR_NOTE: NoteBlock = {
  t: 'note',
  tone: 'r',
  kicker: 'Invariant 11b',
  title: 'This queue cannot tell an orchestrator from a platform',
  body: 'The same orchestrator legitimately means different platforms for different customers — "who pays you through MassPay?" has one answer per customer and no global one. This read carries no flag distinguishing the two, so a row that looks promotable may be a route rather than a payer, and promoting it would attribute one customer\'s income to everyone else behind the same route. Per-user mappings are per-customer state and no path may promote one.',
  ref: 'FR-3.3b · per-user orchestrator mappings are never global',
}

/**
 * What the design draws here and this read cannot fill.
 *
 * Named individually rather than as "some data is unavailable", because the
 * four absences lead to different operator behaviour: no value means the
 * "what is this costing us" sort is unavailable, no confidence means the
 * evidence has to be judged from the signal alone, and no promote control means
 * the work happens elsewhere.
 */
const NOT_SHOWN_NOTE: NoteBlock = {
  t: 'note',
  tone: 'a',
  kicker: 'Not shown',
  title: 'Three things the design draws that this read does not answer for',
  body: 'Distinct customers behind a candidate · the total value of the credits behind it · the match confidence and the per-signal evidence. All three are columns with no field behind them, so they are absent rather than blank or zero. The promotion evidence panel the design draws beside the composer is design-corpus data and stays unrendered for the same reason: invented evidence next to a real candidate id is worse than none.',
  ref: 'GET /v1/admin/catalog/candidates is the whole of this read',
}

/**
 * No rows came back and the depth is not readable either.
 *
 * This is NOT the empty queue, and the difference is the whole reason the
 * endpoint answers the depth from a separate `count(*)`. "Nothing came back"
 * with a depth of nought is a queue an operator can close the tab on; "nothing
 * came back" with no depth at all is a page that may be the front of a backlog
 * of four hundred. Rendering the green note here would have the console
 * assert — in the calm tone, in words — that a count it never read agrees.
 *
 * Reachable on an absent `total`, a `total` past exact integer range, and a
 * negative one, all of which withhold the depth while the rows stay empty.
 */
const UNCOUNTED_EMPTY_NOTE: NoteBlock = {
  t: 'note',
  tone: 'a',
  kicker: 'Empty page, unread depth',
  title: 'No candidates came back, and the queue depth could not be read',
  body: 'The page returned nothing, and the separate count that would confirm the queue is actually clear was not readable. So this is not an empty queue — it is a queue of unknown size that handed over none of itself. Do not read the absence of rows as the absence of work.',
  ref: 'an empty candidates array with no readable total',
}

/** The queue is empty and that is a real, readable answer. */
const EMPTY_NOTE: NoteBlock = {
  t: 'note',
  tone: 'g',
  kicker: 'Empty',
  title: 'No candidates are waiting',
  body: 'The queue was read and returned nothing, and the depth agrees. Every distinct unrecognised payer has been promoted or dismissed. This is an empty queue, not a failed read — a failed read renders as a single note and no table at all.',
  ref: 'read · promoted_at IS NULL AND dismissed_at IS NULL',
}

/**
 * The queue says it has depth and returned no rows.
 *
 * Two queries, one table, so a race is conceivable — but the honest reading of
 * "there are 34 and here are none of them" is that the page is wrong, not that
 * the queue is clear, and an operator must not close the tab on it.
 */
function contradictionNote(depth: number): NoteBlock {
  return {
    t: 'note',
    tone: 'r',
    kicker: 'Contradiction',
    title: `The queue reports ${String(depth)} open candidates and returned none of them`,
    body: 'The depth and the page disagree, and the page is the side that came back empty. This is not an empty queue: something is waiting that this read did not hand over. Do not treat the absence of rows as the absence of work.',
    ref: 'total > 0 with an empty candidates array',
  }
}

function withheldNote(withheld: readonly string[]): NoteBlock {
  return {
    t: 'note',
    tone: 'a',
    kicker: 'Partly unreadable',
    title: `${String(withheld.length)} thing(s) in this queue could not be stated`,
    body: withheld.join(' · '),
    ref: 'A dash is not a zero, and an unreadable row is still a row.',
  }
}

// ---------------------------------------------------------------------------
// Composition
// ---------------------------------------------------------------------------

/**
 * A shape this console knows.
 *
 * Only `candidates` is required to be an array. A missing or non-numeric
 * `total` is a DEGRADATION and not an unknown shape — the rows are still
 * readable and worth showing, and the depth is the one figure withheld. Failing
 * the whole screen over it would trade a full queue for a single note.
 */
function isCandidateQueue(payload: unknown): payload is CandidateQueueResponse {
  if (typeof payload !== 'object' || payload === null) return false
  const r = payload as Partial<CandidateQueueResponse>
  return Array.isArray(r.candidates)
}

/**
 * The read time, stamped from the clock that was handed in.
 *
 * This endpoint sends NO server-side timestamp — unlike the dashboard's
 * `generated_at`, there is simply no field. The choices were to leave the
 * screen with no freshness at all, or to stamp when the console received the
 * payload and say that is what it is. Freshness is structural on this console
 * (see `provenance.ts`), and an unstamped queue is one an operator leaves open
 * across a shift and re-reads at noon; so it is stamped, and the caveat travels
 * inside the stamp rather than in a comment nobody renders.
 *
 * The clock is a parameter for the same reason every pure helper here takes
 * one — no ambient `new Date()` inside the mapping, so the whole thing is
 * table-testable.
 */
function readAtFor(receivedAt: Date): string {
  return `${readAtStamp(receivedAt)} (console receive time — the endpoint sends no server read time)`
}

/**
 * Folds the queue into the blocks the screen renders.
 *
 * Pure, and separate from the fetch, so every absence path is table-testable
 * with no client.
 *
 * `receivedAt` is when the console got the payload. It is not read from a
 * global clock and it is not derived from the rows — `last_seen_at` is the age
 * of the newest observation, which is a different quantity that would read as
 * freshness and be wrong by weeks.
 */
export function composeCandidates(
  response: CandidateQueueResponse,
  receivedAt: Date,
): ScreenData {
  const reads = response.candidates.map(readRow)
  const rows = reads.map((r) => r.cells)

  const depthRaw = exactInteger(response.total)
  // A negative depth is not a depth. Treated as unreadable rather than clamped
  // to zero, because zero is the one answer that means "nothing is waiting".
  const depth = depthRaw !== null && depthRaw >= 0 ? depthRaw : null

  const blocks: Block[] = [tableBlock(rows, depth)]

  if (rows.length === 0) {
    // Three different empties, and only one of them is good news. The green
    // note is reserved for the one where BOTH the page and the depth were read
    // and both said nothing — an unread depth is not agreement.
    blocks.push(
      depth === null
        ? UNCOUNTED_EMPTY_NOTE
        : depth > 0 ? contradictionNote(depth) : EMPTY_NOTE,
    )
  }

  blocks.push(EVIDENCE_NOTE, ORCHESTRATOR_NOTE, NOT_SHOWN_NOTE)

  const withheld = reads.flatMap((r) => r.withheld)
  if (withheld.length > 0) blocks.push(withheldNote(withheld))

  // These four are unanswered on every successful read, so this screen is
  // permanently `partial` — the same standing state the dashboard is in, and
  // for the same reason. The alternative was to badge it `Live` once the rows
  // parse cleanly, which claims that everything on the screen was read: it was
  // not, and the columns missing are the ones that decide which candidate an
  // operator works first.
  const missing: string[] = [
    'distinct customers behind each candidate (this read counts observations, not people)',
    'the value of the credits behind each candidate (this read carries no amounts)',
    'match confidence and per-signal evidence (this read carries neither)',
    'whether a candidate is an orchestrator rather than a platform (invariant 11b — this read cannot say)',
  ]
  if (depth === null) missing.push('the queue depth (this page may not be all of it)')
  missing.push(...withheld)

  const provenance: DataSource = {
    kind: 'partial',
    source: SOURCE,
    authority: 'operational',
    readAt: readAtFor(receivedAt),
    missing,
  }

  // A row opens into its own evidence and the two decisions (#204). The key is
  // the id and nothing else, and a row whose id could not be stated exactly
  // gets `null` — the console opens no row for a null key, so the refusal the
  // table already makes about copied ids reaches the controls for free.
  //
  // Derived through `readCandidate`, which is the same parse the CONTROL will
  // make when the row is opened. Deriving it separately here would be a second
  // reading of the same row, and the two could disagree about which candidate a
  // key names — on a screen whose whole hazard is an id that promotes the wrong
  // one. A row that is not a candidate object at all — `null` among the
  // candidates is a shape this endpoint can produce — is refused there rather
  // than reached for here.
  const rowKeys = response.candidates.map((c) => {
    const parsed = readCandidate(c)
    return parsed === null ? null : String(parsed.id)
  })

  return { blocks, provenance, rowKeys }
}

/**
 * The screen when the queue itself could not be read.
 *
 * One note and nothing else — no table, not even an empty one. An empty table
 * on this screen reads as an empty queue, and "no candidates are waiting" is
 * the single most misleading sentence this screen can produce when the truth is
 * that nobody asked successfully. Falling back to the corpus would be worse
 * again: seven invented candidate ids, one of them marked promoted.
 *
 * `readAt` is empty on purpose. Nothing was read, so there is nothing to date.
 */
function unreadableScreen(reason: string, decisionRef: string | null): ScreenData {
  const ref = decisionRef === null
    ? 'Nothing on this screen was read.'
    : `decision_ref ${decisionRef}`

  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: 'Candidate queue',
      title: 'This queue could not be read',
      body: `${reason} No candidate, no occurrence count and no queue depth is known. That is different from the queue being empty, and this screen shows neither rows nor a count rather than letting the two look alike.`,
      ref,
    }],
    provenance: {
      kind: 'partial',
      source: SOURCE,
      authority: 'unattributed',
      readAt: '',
      missing: ['every candidate, and the depth of the queue behind them'],
    },
  }
}

/**
 * Reads the candidate queue.
 *
 * Resolves for every answer the backend gives, per the loader contract: a
 * refusal and a degraded read are both screens an operator can act on, and only
 * an `AbortError` propagates, so a navigation away stays a cancellation.
 *
 * No `limit` is sent. The backend defaults to its own page cap and rejects
 * anything outside 1..100, so choosing a number here would only ever ask for
 * less than the server is willing to give; the footer states what fraction of
 * the depth arrived.
 *
 * This loader reads and only reads. Promotion and dismissal are not absent from
 * the console any more (#204) — they hang off an OPENED row, in
 * `catalogcontrols.ts`, and re-read the queue at the moment of the decision so
 * that a candidate somebody else has just decided is refused here rather than
 * at the click.
 */
export const loadCandidates: ScreenLoader = async (api) => {
  let payload: unknown
  try {
    payload = await api.get<unknown>(CANDIDATES_PATH)
  } catch (error) {
    if (error instanceof ApiError) {
      // The refusal keeps its `decision_ref` (law 13). A 403 with none is said
      // plainly rather than papered over with a synthesised reference — an
      // invented ref resolves to nothing in the decision explorer and costs a
      // support agent the call.
      return unreadableScreen(
        `The candidate queue answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
        error.decisionRef,
      )
    }
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return unreadableScreen('The backend could not be reached.', null)
  }

  if (!isCandidateQueue(payload)) {
    return unreadableScreen(
      'The candidate queue returned a shape this console does not know.',
      null,
    )
  }
  return composeCandidates(payload, new Date())
}
